<template>
  <div class="ccrs-results">
    <div class="ccrs-results__competitor-design">
      <div class="ccrs-results__competitor-design__headline h5">
        {{ $tv('CompetitorDesign', 'ccrs') }}
      </div>
      <div class="ccrs-results__competitor-design__header">
        <span>{{ $tv('Provider', 'ccrs') }}</span>
        <span>{{ $tv('category', 'breadcrumbs') }}</span>
        <span>{{ $tv('design', 'categories') }}</span>
      </div>
      <div class="ccrs-results__competitor-design__row">
        <span>{{ competitorName }}</span>
        <span>{{ categoryTechnicalName }}</span>
        <span>{{ competitorDesignName }}</span>
      </div>
    </div>
    <div class="ccrs-results__fst-design">
      <CcrsTable
        :headers="headTable"
        :data="mappedData"
        :headline="$tv('FreudenbergDesign', 'ccrs')"
        class="ccrs-results__fst-design__table"
      >
        <template
          #item-image="{ item }"
        >
          <img
            class="ccrs-table-image"
            :src="imageUrl(item.image)"
            :alt="$tv(item.design, 'categories')"
          >
        </template>
        <template #item-brand="{ item }">
          <span>
            {{ $tv(item.brand, 'brands') }}
          </span>
          <InfoIcon popup-ref="showBrandsPopup" />
        </template>
        <template #item-category="{ item }">
          <span>
            {{ $tv(item.category, 'categories') }}
          </span>
        </template>
        <template #item-design="{ item }">
          <span>{{ $tv(item.design, 'categories') }}</span>
          <a
            @mouseover="setIconIndex(item.index)"
            @mouseleave="setIconIndex()"
            v-if="item.matching_condition"
          >
            <ActionInfoFilledIcon
              class="tooltip-icon"
            />
          </a>
          <ToolTip
            class="fst-design-tooltip"
            :show-tool-tip="selectedIcon == item.index"
            tool-tip-position="bottom"
            arrow-position-vertically="top"
            arrow-position-horizontally="left"
          >
            <span>
              {{ $tv(item.matching_condition, 'categories') }}
            </span>
          </ToolTip>
        </template>
        <template #item-action="{item}">
          <nuxt-link
            :to="getLink(item)"
            :title="$tv('design_details', 'ccrs')"
          >
            <span @click="clickItem(item)"><ReadMoreIcon class="show-more" /></span>
          </nuxt-link>
        </template>
      </CcrsTable>
    </div>
    <div class="ccrs-results__disclaimer">
      {{ $tv('Disclaimer', 'ccrs') }}
    </div>
  </div>
</template>
<script>
import InfoIcon from '~/components/InfoIcon.vue'
import {CATEGORIES} from '~/assets/js/constants'
import {getUrlFromTechnicalName} from '~/mixins/category_urls'
import itemImageUrlMixin from '~/mixins/item_image_url'
import ActionInfoFilledIcon from '~/assets/icons/action_info_filled.svg?component'
import ToolTip from '~/components/ToolTip.vue'
import ReadMoreIcon from '~/assets/icons/read_more.svg?component'
import CcrsTable from '~/components/ccrs/CcrsTable.vue'

export default {
  mixins: [itemImageUrlMixin],
  components: {
    InfoIcon,
    ActionInfoFilledIcon,
    ToolTip,
    ReadMoreIcon,
    CcrsTable
  },
  data() {
    return {
      selectedIcon: null,
      headTable: {
        image: '',
        brand: this.$tv('brand', 'categories'),
        category: this.$tv('category', 'breadcrumbs'),
        design: this.$tv('design', 'categories'),
        action: this.$tv('productDetails', 'ccrs')
      }
    }
  },
  props: {
    searchResults: {
      type: Array,
      default() {
        return []
      }
    },
    searchTerm: {
      type: String,
      default() {
        return ''
      }
    },
    chosenCompetitor: {
      type: Object,
      default() {
        return {
          ccrs_competitor: {Name: null},
          ccrs_competitordesign: {Name: null},
          category: {TechnicalName: null}
        }
      }
    }
  },
  methods: {
    getLink(item) {
      return this.localePath(
        `${CATEGORIES.RootCategoriesUrl}/${getUrlFromTechnicalName(
          item?.category,
          this.$i18n.locale
        )}/${item?.design}`, this.$i18n.locale
      )
    },
    setIconIndex(index = null) {
      this.selectedIcon = index
    },
    clickItem(item) {
      this.$gtm.trackEvent({
        event: 'ccrs_detailclick',
        category: 'ccrs_search',
        action: 'ccrs_detailclick',
        ecommerce: {
          'search_term': this.searchTerm,
          'ccrs_design': `${this.competitorName} / ${this.competitorDesignName}`,
          'design': item.design}
      })
    }
  },
  computed: {
    mappedData() {
      const mapped = this.searchResults.map((result, index) => ({
        image: result?.fst_design,
        brand: result?.fst_design.Brands[0],
        category: result.category.TechnicalName,
        design: result?.fst_design.Name,
        matching_condition: result?.matching_condition,
        action: 'null',
        index
      }))

      const sorted = mapped.sort((a, b) => (b.brand === 'F') - (a.brand === 'F'))
      return sorted
    },
    competitorName() {
      return this.chosenCompetitor?.ccrs_competitor?.Name ?? ''
    },
    competitorDesignName() {
      return this.chosenCompetitor?.ccrs_competitordesign?.Name ? this.$tv(this.chosenCompetitor.ccrs_competitordesign.Name, 'categories') : ''
    },
    categoryTechnicalName() {
      return this.chosenCompetitor?.category?.TechnicalName ? this.$tv(this.chosenCompetitor.category.TechnicalName, 'categories') : ''
    }
  }
}
</script>
<style lang="scss">
$margin: $default-margin-padding-width * 1.5;
$td-width: 19.138rem;
$first-td-width: 5rem;

.ccrs-results {
  display: flex;
  flex-direction: column;
  padding-bottom: $default-margin-padding-width;
  margin: $default-margin-padding-2x $default-margin-padding-2x 0;

  img.ccrs-table-image {
    max-width: 4rem;
    max-height: 4rem;
    margin-right: $default-margin-padding-width / 2;
  }

  .ccrs-results__competitor-design {
    display: flex;
    flex-direction: column;

    &__headline {
      margin-bottom: $margin;
    }

    &__header {
      display: flex;
      flex-flow: row;
      margin-bottom: $margin;

      @include breakpoint-up($md) {
        span:first-child {
          width: $td-width + $first-td-width - 1.1rem;
        }

        span:nth-child(2) {
          width: $td-width - 1.5rem;
        }
      }

      span {
        color: $color__primary;
        width: $td-width;
        margin-right: $margin;
      }
    }

    &__row {
      display: flex;
      flex-flow: row;
      margin-bottom: $margin;

      @include breakpoint-up($md) {
        span:first-child {
          width: $td-width + $first-td-width - 1.1rem;
        }

        span:nth-child(2) {
          width: $td-width - 1.5rem;
        }
      }

      span {
        color: $color__text__default;
        width: $td-width;
        margin-right: $margin;
      }
    }
  }

  .ccrs-results__fst-design {
    display: flex;
    flex-direction: column;

    .tooltip-icon.icon--svg {
      position: relative;
      cursor: pointer;
      font-size: 1.25rem;
      color: $color__primary;
      top: -.3125rem;
      left: -.2rem;
    }

    .fst-design-tooltip {
      width: 14rem;
      top: auto;
      margin-top: .3125rem;

      &.arrow.arrow-left {
        &::after {
          left: 1.66rem;
        }
      }
    }

    .icon.infoIcon {
      position: relative;
      top: -.3125rem;
      left: -.2rem;
    }

    .show-more.icon--svg {
      position: relative;
      cursor: pointer;
      font-size: 1.8rem;
      color: $color__primary;
    }

    &__mobileView {
      &__row {
        border-bottom: .0625rem solid $color__primary;
        display: flex;
        flex-direction: column;
        margin-bottom: 1rem;

        &__entry {
          display: flex;
          flex-direction: row;
          align-items: baseline;

          &__header {
            width: 6rem;
            margin-bottom: 1rem;
            margin-right: 2rem;
            text-align: left;
            color: $color__primary;
            font-size: $base-text-font-size;
            font-weight: $base-text-font-weight-semi-bold;
          }
        }
      }
    }

    &__table {
      margin-bottom: 1rem;

      table {
        width: 100%;
        border-collapse: collapse;

        thead {
          th {
            text-align: left;
            color: $color__primary;
            font-size: $base-text-font-size;
            font-weight: $base-text-font-weight-semi-bold;

            &:first-child {
              width: 3.9rem;
            }
          }
        }

        tbody {
          &::before {
            line-height: 1em;
            content: '\200C';
            display: block;
          }

          tr {
            padding-bottom: $default-margin-padding-width * .25;
            border-bottom: .0625rem solid $color__primary;

            &::after {
              line-height: 2.9rem;
              content: '\200C';
              display: block;
            }

            td {
              color: $color__text__default;
              width: $td-width;
              margin-right: $margin;
              overflow-wrap: break-word;

              &:first-child {
                width: $first-td-width;
              }

              &:last-child {
                margin-right: 0;
                width: 4rem;
              }
            }
          }
        }
      }
    }

    &__headline {
      margin-bottom: $margin;
    }
  }

  .ccrs-results__disclaimer {
    padding: $default-margin-padding-width $default-margin-padding-width-small;
    color: $color__primary;
    border: .0625rem solid $color__primary;
  }

  @include breakpoint-down($sm) {
    .ccrs-results__competitor-design {
      &__header {
        span {
          width: 33%;
          margin-right: $default-margin-padding-width;
        }
      }

      &__row {
        span {
          width: 33%;
          margin-right: $default-margin-padding-width;
        }
      }
    }
  }
}
</style>
